import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { useAccommodationUser } from './accommodationUser';
import {
  publishAccommodationChange,
  deleteAccommodationDocument,
  getAccommodationDocumentViewUrl,
  getAccommodationScopeInfo,
  listAccommodationDocuments,
  uploadAccommodationDocument,
} from './studentAccommodationsApi';

const TITLE = 'EMERGENCY ACCOMMODATIONS FOR STUDENTS WITH ACCESS & FUNCTIONAL NEEDS';

function clean(value) {
  return String(value ?? '').trim();
}

function errorText(error, fallback) {
  return error?.response?.data?.error || error?.message || fallback;
}

function bytesLabel(value) {
  const bytes = Number(value || 0);
  if (!bytes) return '—';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function scopeLabel(row, info) {
  if (row.scopeType === 'account') return 'District';
  if (row.scopeType === 'campus') return `Campus${info?.campusName ? ` — ${info.campusName}` : ''}`;
  if (row.scopeType === 'building') return `Building${info?.buildingName ? ` — ${info.buildingName}` : ''}`;
  return row.scopeType || '—';
}

export default function EmergencyAccommodationDocumentModal(props) {
  const { user } = useAccommodationUser(props.userOverride);
  const identity = [user?.accountID, user?.userID, user?.userRole].join('|');
  return <EmergencyAccommodationDocumentSession key={identity} {...props} />;
}

function EmergencyAccommodationDocumentSession(props) {
  const contextKey = [props.scope?.iAccountID, props.scope?.iCampusID, props.scope?.iBuildingID,
    props.scope?.incidentID, Boolean(props.show)].join('|');
  const [session, setSession] = useState({ key: contextKey, scope: props.scope, show: props.show });
  const guardRef = useRef(null);
  useEffect(() => {
    if (contextKey === session.key) return;
    const apply = () => setSession({ key: contextKey, scope: props.scope, show: props.show });
    if (guardRef.current) guardRef.current(apply);
    else apply();
    // Keep the original upload destination until pending work is resolved.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextKey]);
  return <EmergencyAccommodationDocumentModalContent key={session.key} {...props} scope={session.scope} show={session.show} guardRef={guardRef} />;
}

function EmergencyAccommodationDocumentModalContent({ show, onHide, scope, readOnly = false, userOverride, autoOpen = true, guardRef, requestCloseRef }) {
  const { user } = useAccommodationUser(userOverride);
  const [serverCanEdit, setServerCanEdit] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const deferredActionRef = useRef(null);
  const activeRef = useRef(true);
  const sequenceRef = useRef(0);
  useEffect(() => {
    activeRef.current = true;
    return () => { activeRef.current = false; sequenceRef.current += 1; };
  }, []);
  const effectiveScope = useMemo(() => ({
    ...scope,
    districtSearch: false,
    readOnly: Boolean(readOnly || scope?.readOnly === true || scope?.readOnly === 'true' || scope?.readOnly === 1 || scope?.readOnly === '1'),
    iAccountID: clean(scope?.iAccountID || user?.accountID),
    iCampusID: clean(scope?.iCampusID) || '-99',
    iBuildingID: clean(scope?.iBuildingID),
  }), [scope, user?.accountID, readOnly]);
  const canEdit = loaded && serverCanEdit && !effectiveScope.readOnly;

  const [scopeInfo, setScopeInfo] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [resolved, setResolved] = useState(null);
  const [scopeType, setScopeType] = useState('building');
  const [file, setFile] = useState(null);
  const [viewerUrl, setViewerUrl] = useState('');
  const [viewerName, setViewerName] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  async function reload() {
    const sequence = ++sequenceRef.current;
    if (!effectiveScope.iAccountID || !effectiveScope.iBuildingID || effectiveScope.iBuildingID === '-99') {
      setError('Select a building to view its accommodations PDF.');
      return;
    }
    setLoaded(false);
    setServerCanEdit(false);
    setLoading(true);
    setError('');
    setScopeInfo(null);
    setDocuments([]);
    setResolved(null);
    setViewerUrl('');
    setViewerName('');
    try {
      const [scopeResponse, documentResponse] = await Promise.all([
        getAccommodationScopeInfo(effectiveScope),
        listAccommodationDocuments(effectiveScope),
      ]);
      if (!activeRef.current || sequence !== sequenceRef.current) return;
      setLoaded(true);
      setServerCanEdit(documentResponse?.canEditDocuments === true || documentResponse?.permissions?.canEditDocuments === true);
      setScopeInfo(scopeResponse?.scope || null);
      setDocuments(Array.isArray(documentResponse?.rows) ? documentResponse.rows : []);
      setResolved(documentResponse?.resolved || null);
      if (autoOpen && documentResponse?.resolved) await openDocument(documentResponse.resolved);
    } catch (loadError) {
      if (activeRef.current && sequence === sequenceRef.current) setError(errorText(loadError, 'Unable to load the emergency accommodations PDF configuration.'));
    } finally {
      if (activeRef.current && sequence === sequenceRef.current) setLoading(false);
    }
  }

  useEffect(() => {
    if (!show) return;
    setViewerUrl('');
    setViewerName('');
    setSuccess('');
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, effectiveScope.iAccountID, effectiveScope.iCampusID, effectiveScope.iBuildingID, effectiveScope.incidentID, effectiveScope.readOnly]);

  async function openDocument(document) {
    setBusy(true);
    setError('');
    setViewerUrl('');
    setViewerName('');
    try {
      const response = await getAccommodationDocumentViewUrl(document.documentId, effectiveScope);
      if (!activeRef.current) return;
      if (!response?.url) throw new Error('The PDF preview could not be opened. Try View again.');
      setViewerUrl(response.url);
      setViewerName(document.originalFileName || TITLE);
    } catch (openError) {
      if (activeRef.current) setError(errorText(openError, 'Failed to open the PDF.'));
    } finally {
      if (activeRef.current) setBusy(false);
    }
  }

  async function uploadDocument() {
    if (!canEdit || busy) return false;
    if (!file) {
      setError('Choose a PDF file first.');
      return;
    }
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      await uploadAccommodationDocument(
        effectiveScope,
        scopeType,
        file,
        scopeInfo?.iCampusID || effectiveScope.iCampusID
      );
      setFile(null);
      setSuccess('Emergency accommodations PDF saved.');
      publishAccommodationChange(effectiveScope);
      await reload();
      return true;
    } catch (uploadError) {
      if (activeRef.current) setError(errorText(uploadError, 'Failed to upload the PDF.'));
      return false;
    } finally {
      if (activeRef.current) setBusy(false);
    }
  }

  async function removeDocument(document) {
    if (!canEdit || busy) return;
    if (!window.confirm(`Remove ${scopeLabel(document, scopeInfo)} PDF: ${document.originalFileName}?`)) return;
    setBusy(true);
    setError('');
    setSuccess('');
    try {
      await deleteAccommodationDocument(document.documentId, effectiveScope);
      setViewerUrl('');
      setViewerName('');
      setSuccess('Emergency accommodations PDF removed.');
      publishAccommodationChange(effectiveScope);
      await reload();
    } catch (deleteError) {
      if (activeRef.current) setError(errorText(deleteError, 'Failed to remove the PDF.'));
    } finally {
      if (activeRef.current) setBusy(false);
    }
  }

  function requestTransition(action = onHide) {
    if (busy) { deferredActionRef.current = action; return; }
    if (file) setPendingAction(() => action);
    else action();
  }
  function close() { requestTransition(); }
  guardRef.current = requestTransition;
  if (requestCloseRef) requestCloseRef.current = requestTransition;
  useEffect(() => {
    if (!busy && deferredActionRef.current) {
      const action = deferredActionRef.current;
      deferredActionRef.current = null;
      requestTransition(action);
    }
    // Use the current file state after an in-progress upload finishes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busy]);
  useEffect(() => {
    const warn = (event) => { if (file || busy) { event.preventDefault(); event.returnValue = ''; } };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [file, busy]);

  const visibleDocuments = documents.filter((row) => {
    if (row.scopeType === 'account') return true;
    if (row.scopeType === 'building') return clean(row.scopeValue) === clean(effectiveScope.iBuildingID);
    if (row.scopeType === 'campus') return clean(row.scopeValue) === clean(scopeInfo?.iCampusID);
    return false;
  });

  return (
    <>
    <Modal show={show} onHide={busy ? undefined : close} keyboard={!busy} backdrop={busy ? "static" : true} size="xl" centered scrollable>
      <Modal.Header closeButton={!busy}>
        <Modal.Title>{TITLE}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-2">
          This is the building, campus, or District emergency accommodations procedure PDF. Student-specific plans are stored separately.
        </p>
        {scopeInfo && (
          <div className="text-muted small mb-3">
            Campus: {scopeInfo.campusName || scopeInfo.iCampusID || '—'} · Building: {scopeInfo.buildingName || effectiveScope.iBuildingID}
          </div>
        )}
        {error && <Alert variant="danger">{error}{!loaded && <Button size="sm" className="ms-2" disabled={busy || loading} onClick={reload}>Retry</Button>}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        {loading ? (
          <div className="text-center py-4"><Spinner animation="border" size="sm" /> Loading…</div>
        ) : loaded ? (
          <>
            <div className="border rounded p-3 mb-3">
              <strong>PDF used for this building:</strong>{' '}
              {resolved ? (
                <>
                  <Badge bg="primary">{scopeLabel(resolved, scopeInfo)}</Badge>{' '}
                  {resolved.originalFileName}{' '}
                  <Button size="sm" variant="outline-primary" disabled={busy} onClick={() => openDocument(resolved)}>View</Button>
                </>
              ) : (
                <span className="text-muted">No PDF configured for this building.</span>
              )}
              <div className="small text-muted mt-1">Resolution order: Building → Campus → District.</div>
            </div>

            {canEdit && (
              <fieldset disabled={busy} className="border rounded p-3 mb-4">
                <h6>Upload / Replace PDF</h6>
                <div className="d-flex flex-wrap gap-2 align-items-end">
                  <Form.Group style={{ minWidth: 220 }}>
                    <Form.Label>Scope</Form.Label>
                    <Form.Select value={scopeType} onChange={(e) => setScopeType(e.target.value)}>
                      <option value="building">Current building</option>
                      <option value="campus" disabled={!scopeInfo?.iCampusID || scopeInfo.iCampusID === '-99'}>Current campus</option>
                      <option value="account">Entire District</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group style={{ minWidth: 320, flex: 1 }}>
                    <Form.Label>PDF file</Form.Label>
                    <Form.Control type="file" accept="application/pdf,.pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                  </Form.Group>
                  <Button disabled={busy || !file} onClick={uploadDocument}>Upload PDF</Button>
                </div>
              </fieldset>
            )}

            <Table responsive bordered size="sm">
              <thead><tr><th>Scope</th><th>File</th><th>Size</th><th>Uploaded by</th><th>Updated</th><th>Actions</th></tr></thead>
              <tbody>
                {visibleDocuments.length === 0 ? (
                  <tr><td colSpan={6} className="text-center text-muted">No applicable PDFs configured.</td></tr>
                ) : visibleDocuments.map((row) => (
                  <tr key={row.documentId}>
                    <td>{scopeLabel(row, scopeInfo)}</td>
                    <td>{row.originalFileName}</td>
                    <td>{bytesLabel(row.fileSize)}</td>
                    <td>{row.uploadedByName || row.uploadedByUserID || '—'}</td>
                    <td>{row.updatedAt ? new Date(row.updatedAt).toLocaleString() : '—'}</td>
                    <td className="d-flex gap-2">
                      <Button size="sm" variant="outline-primary" disabled={busy} onClick={() => openDocument(row)}>View</Button>
                      {canEdit && <Button size="sm" variant="outline-danger" disabled={busy} onClick={() => removeDocument(row)}>Remove</Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {viewerUrl && (
              <div className="mt-3">
                <div className="d-flex justify-content-between align-items-center mb-2"><strong>{viewerName}</strong><Button size="sm" variant="outline-secondary" onClick={() => setViewerUrl('')}>Close Preview</Button></div>
                <iframe title={viewerName || TITLE} src={viewerUrl} style={{ width: '100%', height: '70vh', border: '1px solid #cbd5e1', borderRadius: 6 }} />
              </div>
            )}
          </>
        ) : null}
      </Modal.Body>
      <Modal.Footer><Button variant="secondary" disabled={busy} onClick={close}>Close</Button></Modal.Footer>
    </Modal>
    <Modal show={Boolean(pendingAction)} onHide={() => !busy && setPendingAction(null)} centered backdrop="static">
      <Modal.Header><Modal.Title>PDF not uploaded</Modal.Title></Modal.Header>
      <Modal.Body>Upload the selected PDF to this building or scope before continuing?{error && <Alert variant="danger" className="mt-2">{error}</Alert>}</Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" disabled={busy} onClick={() => setPendingAction(null)}>Keep Editing</Button>
        <Button variant="outline-danger" disabled={busy} onClick={() => { const action = pendingAction; setFile(null); setPendingAction(null); if (action) action(); }}>Discard</Button>
        <Button disabled={busy || !canEdit || !file} onClick={async () => { const action = pendingAction; if (await uploadDocument()) { setPendingAction(null); if (action) action(); } }}>Save</Button>
      </Modal.Footer>
    </Modal>
    </>
  );
}
